import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus, Loader2, Clock, List } from 'lucide-react'
import type { MemberDto, OccurrenceDto, SeriesScope } from '@familynest/shared'
import { api } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import {
  addDays,
  addMonths,
  formatDayLong,
  formatMonthYear,
  formatRange,
  startOfDay,
  startOfMonth,
  startOfWeek,
  weekDays,
} from '../../lib/date'
import { Avatar, Button, ErrorBanner } from '../ui'
import { MonthView } from './MonthView'
import { AgendaView } from './AgendaView'
import { EventDialog, occurrenceKeyOf } from './EventDialog'
import { TimeGridView } from './TimeGridView'
import { useDialogs } from '../ui/dialogs'

type View = 'month' | 'week' | 'day'
type WeekLayout = 'grid' | 'agenda'

// Woche/Tag als Zeitraster oder Agenda-Liste (ADR-088). Präferenz pro Gerät;
// ohne gespeicherte Wahl: Handy → Liste, sonst Raster.
const LAYOUT_KEY = 'fn.calendar.weekLayout'
function initialLayout(): WeekLayout {
  try {
    const v = localStorage.getItem(LAYOUT_KEY)
    if (v === 'grid' || v === 'agenda') return v
  } catch {
    /* Storage gesperrt → Standard */
  }
  return typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches ? 'agenda' : 'grid'
}

function windowFor(view: View, cursor: Date): { from: Date; to: Date } {
  if (view === 'month') {
    const from = startOfWeek(startOfMonth(cursor))
    return { from, to: addDays(from, 42) }
  }
  if (view === 'week') {
    const from = startOfWeek(cursor)
    return { from, to: addDays(from, 7) }
  }
  const from = startOfDay(cursor)
  return { from, to: addDays(from, 1) }
}

function title(view: View, cursor: Date): string {
  if (view === 'month') return formatMonthYear(cursor)
  if (view === 'day') return formatDayLong(cursor)
  const days = weekDays(cursor)
  return `${formatRange(days[0]!)} – ${formatRange(days[6]!)}`
}

/**
 * Kalender-Screen für den EINEN Familienkalender (ADR-026). Termine gehören zur
 * Familie; Zugehörigkeit läuft über die Teilnahme (teilnehmend / nur Info).
 * Farbe eines Termins = Personenfarbe des/der ersten Teilnehmenden. Rechts eine
 * Personen-Legende zum Filtern der Anzeige. Anlegen: alle außer Gast (Kind kann
 * eigene Termine anlegen) — Enforcement bleibt am Server.
 */
export function CalendarScreen({ members }: { members: MemberDto[] }): React.JSX.Element {
  const { member } = useAuth()
  const [view, setView] = useState<View>('month')
  const [cursor, setCursor] = useState<Date>(() => new Date())
  const [occ, setOcc] = useState<OccurrenceDto[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [dialog, setDialog] = useState<{ editing: OccurrenceDto | null; date: Date; exactTime?: boolean } | null>(null)
  const [filter, setFilter] = useState<Set<string>>(new Set()) // leer = alle anzeigen
  const [layout, setLayout] = useState<WeekLayout>(initialLayout)
  const [moveError, setMoveError] = useState<string | null>(null)
  const dialogs = useDialogs()

  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])
  const accent = '#5E6E43'

  // Farbe eines Termins: Personenfarbe des ersten Teilnehmenden (bevorzugt
  // „teilnehmend"), sonst Marken-Akzent für allgemeine Familientermine.
  const colorFor = useCallback(
    (o: OccurrenceDto): string => {
      if (o.color) return o.color // aus externem iCal-Abo (eigene Abo-Farbe)
      const first = o.attendees.find((a) => a.kind === 'participant') ?? o.attendees[0]
      return (first && memberMap.get(first.memberId)?.color) || accent
    },
    [memberMap],
  )

  const canCreate = !!member && member.role !== 'guest'

  const load = useCallback(async () => {
    setLoading(true)
    const { from, to } = windowFor(view, cursor)
    try {
      setOcc(await api.listOccurrences(from.toISOString(), to.toISOString()))
      setLoadError(false)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [view, cursor])

  useEffect(() => {
    void load()
  }, [load])
  useLive('calendar', load)

  // Personen-Legende zeigt nur Mitglieder, die überhaupt an Terminen beteiligt sind.
  const involved = useMemo(() => {
    const ids = new Set<string>()
    for (const o of occ) for (const a of o.attendees) ids.add(a.memberId)
    return members.filter((m) => ids.has(m.id))
  }, [occ, members])

  const visible = useMemo(() => {
    if (filter.size === 0) return occ
    // gefiltert: Termine der gewählten Personen + allgemeine (ohne Teilnehmer)
    return occ.filter((o) => o.attendees.length === 0 || o.attendees.some((a) => filter.has(a.memberId)))
  }, [occ, filter])

  function toggleFilter(id: string): void {
    setFilter((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function navigate(dir: -1 | 1): void {
    setCursor((c) => (view === 'month' ? addMonths(c, dir) : addDays(c, dir * (view === 'week' ? 7 : 1))))
  }

  function openNew(date: Date): void {
    if (!canCreate) return
    setDialog({ editing: null, date })
  }
  // Klick auf einen Tag in der Monatsansicht → Tagesansicht dieses Datums.
  function openDay(date: Date): void {
    setCursor(date)
    setView('day')
  }
  function openEdit(o: OccurrenceDto): void {
    setDialog({ editing: o, date: new Date(o.startsAt) })
  }
  function openNewAt(at: Date): void {
    if (!canCreate) return
    setDialog({ editing: null, date: at, exactTime: true })
  }
  function switchLayout(l: WeekLayout): void {
    setLayout(l)
    try {
      localStorage.setItem(LAYOUT_KEY, l)
    } catch {
      /* egal */
    }
  }

  // Schreibrecht spiegelt den Server (Enforcement bleibt dort): Abo-Termine nie,
  // Erwachsene alles, Kind nur eigene, Gast nie.
  const canDrag = useCallback(
    (o: OccurrenceDto): boolean => {
      if (!member || o.subscriptionId) return false
      if (member.role === 'adult') return true
      return member.role === 'child' && o.createdBy === member.id
    },
    [member],
  )

  /**
   * Drag & Drop im Zeitraster (ADR-088): bei Serien erst fragen, worauf sich
   * das bezieht (ADR-087); dann optimistisch umsetzen, Server-Aufruf, bei Fehler
   * zurückrollen. Das WebSocket-Notify lädt danach für alle Geräte neu.
   */
  async function moveOccurrence(o: OccurrenceDto, start: Date, end: Date): Promise<void> {
    let scope: SeriesScope = 'all'
    if (o.rrule) {
      const v = await dialogs.choose({
        title: 'Serientermin verschieben',
        options: [
          { value: 'this', label: 'Nur dieser Termin' },
          { value: 'following', label: 'Dieser und alle folgenden' },
          { value: 'all', label: 'Alle Termine der Serie' },
        ],
      })
      if (!v) return
      scope = v as SeriesScope
    }
    const before = occ
    const startsAt = start.toISOString()
    const endsAt = end.toISOString()
    setMoveError(null)
    setOcc((list) => list.map((x) => (x === o ? { ...x, occurrenceStart: startsAt, occurrenceEnd: endsAt } : x)))
    try {
      if (!o.rrule) {
        await api.updateEvent(o.id, { startsAt, endsAt })
      } else if (scope === 'all') {
        // Serienbeginn um dieselbe Differenz verschieben wie das gezogene Vorkommen.
        const delta = start.getTime() - Date.parse(o.occurrenceStart)
        const base = Date.parse(o.startsAt) + delta
        await api.updateEvent(o.id, { startsAt: new Date(base).toISOString(), endsAt: new Date(base + (end.getTime() - start.getTime())).toISOString() })
      } else {
        await api.updateEvent(o.id, { scope, occurrenceStart: occurrenceKeyOf(o), startsAt, endsAt })
      }
      void load()
    } catch {
      setOcc(before)
      setMoveError('Verschieben fehlgeschlagen — der Termin steht wieder am alten Platz.')
    }
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3 md:p-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button onClick={() => navigate(-1)} aria-label="Zurück" className="rounded-lg p-2 hover:bg-black/5">
            <ChevronLeft className="size-5" style={{ color: 'var(--color-ink)' }} />
          </button>
          <button onClick={() => navigate(1)} aria-label="Weiter" className="rounded-lg p-2 hover:bg-black/5">
            <ChevronRight className="size-5" style={{ color: 'var(--color-ink)' }} />
          </button>
          <Button variant="ghost" onClick={() => setCursor(new Date())}>
            Heute
          </Button>
        </div>
        <h1 className="text-lg font-semibold capitalize" style={{ color: 'var(--color-ink)' }}>
          {title(view, cursor)}
        </h1>

        <div className="ml-auto flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border" style={{ borderColor: 'var(--color-line)' }}>
            {(['month', 'week', 'day'] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className="px-3 py-1.5 text-sm"
                style={
                  view === v
                    ? { backgroundColor: 'var(--color-accent)', color: '#fff' }
                    : { backgroundColor: 'var(--color-card)', color: 'var(--color-ink)' }
                }
              >
                {v === 'month' ? 'Monat' : v === 'week' ? 'Woche' : 'Tag'}
              </button>
            ))}
          </div>
          {view !== 'month' && (
            <div className="flex overflow-hidden rounded-lg border" style={{ borderColor: 'var(--color-line)' }}>
              {(['grid', 'agenda'] as WeekLayout[]).map((l) => {
                const Icon = l === 'grid' ? Clock : List
                return (
                  <button
                    key={l}
                    onClick={() => switchLayout(l)}
                    aria-label={l === 'grid' ? 'Zeitraster' : 'Liste'}
                    aria-pressed={layout === l}
                    title={l === 'grid' ? 'Zeitraster' : 'Liste'}
                    className="px-2.5 py-1.5"
                    style={layout === l ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-card)', color: 'var(--color-ink)' }}
                  >
                    <Icon className="size-4" />
                  </button>
                )
              })}
            </div>
          )}
          {canCreate && (
            <Button onClick={() => openNew(cursor)}>
              <Plus className="size-4" /> Termin
            </Button>
          )}
        </div>
      </div>

      {/* Inhalt + Personen-Legende */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-[1fr_15rem]">
        <div className="relative min-h-0">
          {loading && (
            <div className="absolute right-2 top-2 z-10">
              <Loader2 className="size-4 animate-spin" style={{ color: 'var(--color-muted)' }} />
            </div>
          )}
          {loadError && <ErrorBanner message="Termine konnten nicht geladen werden." onRetry={() => void load()} />}
          {moveError && <ErrorBanner message={moveError} onRetry={() => setMoveError(null)} />}
          {view === 'month' ? (
            <MonthView cursor={cursor} occurrences={visible} colorFor={colorFor} onDayClick={openDay} onEventClick={openEdit} />
          ) : layout === 'grid' ? (
            <TimeGridView
              mode={view}
              cursor={cursor}
              occurrences={visible}
              colorFor={colorFor}
              canDrag={canDrag}
              canCreate={canCreate}
              onEventClick={openEdit}
              onSlotClick={openNewAt}
              onDayClick={openDay}
              onOverflow={(day) => {
                // „+N": mehr parallele Termine, als lesbar nebeneinander passen → Tagesliste zeigt alle.
                setCursor(day)
                setView('day')
                switchLayout('agenda')
              }}
              onMove={(o, s, e) => void moveOccurrence(o, s, e)}
            />
          ) : (
            <AgendaView mode={view} cursor={cursor} occurrences={visible} colorFor={colorFor} onDayClick={openNew} onEventClick={openEdit} />
          )}
        </div>

        <aside className="hidden rounded-[var(--radius-card)] border p-3 md:block" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
          <h2 className="mb-2 text-sm font-semibold" style={{ color: 'var(--color-ink)' }}>Familienkalender</h2>
          {involved.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Alle Termine der Familie. Tippe „Termin", um Personen als teilnehmend oder nur Info zu markieren.</p>
          ) : (
            <>
              <p className="mb-2 text-xs" style={{ color: 'var(--color-muted)' }}>Anzeige filtern:</p>
              <ul className="flex flex-col gap-1">
                {involved.map((m) => {
                  const on = filter.size === 0 || filter.has(m.id)
                  return (
                    <li key={m.id}>
                      <button
                        onClick={() => toggleFilter(m.id)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-black/5"
                        style={{ opacity: on ? 1 : 0.4 }}
                      >
                        <Avatar name={m.name} color={m.color} size={24} src={m.avatarUrl} />
                        <span style={{ color: 'var(--color-ink)' }}>{m.name}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
              {filter.size > 0 && (
                <button onClick={() => setFilter(new Set())} className="mt-2 text-xs" style={{ color: 'var(--color-accent)' }}>
                  Filter zurücksetzen
                </button>
              )}
            </>
          )}
        </aside>
      </div>

      {dialog && member && (
        <EventDialog
          editing={dialog.editing}
          defaultDate={dialog.date}
          exactTime={dialog.exactTime}
          members={members}
          member={member}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null)
            void load()
          }}
        />
      )}
    </div>
  )
}
